export interface CompatibilityData {
  slug1: string;
  slug2: string;
  title: string;
  score: number;
  verdict: string;
  metaDescription: string;
  intro: string;
  love: string;
  strengths: string;
  challenges: string;
  communication: string;
}

// Element groupings
const ELEMENT: Record<string, string> = {
  aries: 'fogo', leao: 'fogo', sagitario: 'fogo',
  touro: 'terra', virgem: 'terra', capricornio: 'terra',
  gemeos: 'ar', libra: 'ar', aquario: 'ar',
  cancer: 'agua', escorpiao: 'agua', peixes: 'agua',
};

const NAMES: Record<string, string> = {
  aries: 'Áries', touro: 'Touro', gemeos: 'Gêmeos', cancer: 'Câncer',
  leao: 'Leão', virgem: 'Virgem', libra: 'Libra', escorpiao: 'Escorpião',
  sagitario: 'Sagitário', capricornio: 'Capricórnio', aquario: 'Aquário', peixes: 'Peixes',
};

// Base compatibility scores by element pair
function elementScore(e1: string, e2: string): number {
  if (e1 === e2) return 85;
  const fire = new Set(['fogo', 'ar']);
  const earth = new Set(['terra', 'agua']);
  if (fire.has(e1) && fire.has(e2)) return 82;
  if (earth.has(e1) && earth.has(e2)) return 80;
  return 52;
}

// Hardcoded overrides for notable pairs
const SCORE_OVERRIDES: Record<string, number> = {
  'aries-leao': 92, 'aries-sagitario': 90, 'leao-sagitario': 91,
  'touro-virgem': 88, 'touro-capricornio': 87, 'virgem-capricornio': 89,
  'gemeos-libra': 88, 'gemeos-aquario': 86, 'libra-aquario': 87,
  'cancer-escorpiao': 91, 'cancer-peixes': 89, 'escorpiao-peixes': 90,
  'aries-cancer': 55, 'aries-capricornio': 50, 'touro-aquario': 48,
  'touro-leao': 56, 'gemeos-virgem': 54, 'cancer-libra': 53,
  'leao-escorpiao': 58, 'leao-touro': 56, 'virgem-sagitario': 55,
  'libra-capricornio': 54, 'escorpiao-aquario': 49, 'sagitario-peixes': 57,
  'aries-libra': 72, 'touro-escorpiao': 68, 'gemeos-sagitario': 74,
  'cancer-capricornio': 65, 'leao-aquario': 70, 'virgem-peixes': 67,
};

function getScore(s1: string, s2: string): number {
  const key = [s1, s2].sort().join('-');
  if (key in SCORE_OVERRIDES) return SCORE_OVERRIDES[key];
  return elementScore(ELEMENT[s1], ELEMENT[s2]);
}

// Generate text based on elements and score
function buildCompatibility(slug1: string, slug2: string): CompatibilityData {
  const [a, b] = [slug1, slug2].sort();
  const score = getScore(a, b);
  const na = NAMES[a];
  const nb = NAMES[b];
  const ea = ELEMENT[a];
  const eb = ELEMENT[b];
  const sameElement = ea === eb;

  const verdicts: Record<string, string> = {
    high: 'Uma combinação com potencial extraordinário para crescimento mútuo e cumplicidade.',
    mid: 'Uma parceria de contrastes que pode gerar atração e crescimento, com esforço consciente.',
    low: 'Energias opostas que exigem maturidade emocional e comunicação clara para florescer.',
    opposite: 'Polos opostos do zodíaco, atração intensa com desafios igualmente profundos.',
  };
  const verdict = score >= 80 ? verdicts.high : score >= 65 ? verdicts.opposite : score >= 55 ? verdicts.mid : verdicts.low;

  const elementNames: Record<string, string> = { fogo: 'Fogo', terra: 'Terra', ar: 'Ar', agua: 'Água' };

  const introMap: Record<string, string> = {
    'fogo-fogo': `${na} e ${nb} compartilham a mesma chama ardente, uma combinação de entusiasmo, aventura e visão expansiva. A vida a dois nunca é monótona quando dois signos de Fogo se encontram. O risco está no excesso de ego; o presente, na cumplicidade espontânea.`,
    'terra-terra': `${na} e ${nb} constroem juntos com paciência e propósito. Dois signos de Terra sabem o valor da estabilidade e trabalham naturalmente em sintonia. A rotina pode parecer segura demais para o mundo exterior, mas para eles é onde o amor mais profundo cresce.`,
    'ar-ar': `${na} e ${nb} se encontram no território das ideias, da conversa e da troca intelectual. Dois signos de Ar criam uma relação vibrante, estimulante e nunca previsível. O desafio é ancorar o afeto para além da mente.`,
    'agua-agua': `${na} e ${nb} mergulham juntos nas profundezas emocionais com naturalidade rara. Dois signos de Água entendem o não-dito e honram a sensibilidade um do outro. O risco é o fechamento mútuo no casulo, o externo pode parecer ameaçador demais.`,
    'fogo-ar': `${na} e ${nb} formam um par de energia complementar: o Fogo inspira e o Ar expande. Juntos, criam projetos, aventuras e conversas que alimentam ambos. A espontaneidade é a linguagem nativa dessa combinação.`,
    'terra-agua': `${na} e ${nb} criam solo fértil juntos. A Terra oferece estrutura e segurança; a Água, profundidade emocional e nutrição. Uma combinação que tende a se tornar cada vez mais sólida com o tempo.`,
    'fogo-terra': `${na} e ${nb} representam forças distintas que precisam aprender a dançar juntas. O Fogo quer arder; a Terra quer durar. Com paciência mútua, um pode oferecer ao outro exatamente o que falta.`,
    'fogo-agua': `${na} e ${nb} são forças elementares que se atraem e se tensionam em igual medida. O Fogo aquece a Água; a Água pode apagar a chama. Depende da temperatura emocional de cada momento.`,
    'ar-terra': `${na} e ${nb} movem-se em ritmos diferentes: o Ar prefere o movimento, a Terra prefere a raiz. Quando encontram equilíbrio, um oferece praticidade enquanto o outro oferece perspectiva ampla.`,
    'ar-agua': `${na} e ${nb} se complementam em níveis que nem sempre são óbvios. O Ar traz leveza e racionalidade; a Água traz intuição e emoção. Juntos, podem criar uma relação de notável riqueza interior.`,
  };

  const pairKey = [ea, eb].sort().join('-');
  const intro = introMap[`${ea}-${eb}`] || introMap[`${eb}-${ea}`] || introMap[pairKey] || `${na} e ${nb} formam uma combinação de ${elementNames[ea]} e ${elementNames[eb]} com dinâmicas únicas a descobrir juntos.`;

  const love = score >= 80
    ? `O amor entre ${na} e ${nb} flui com naturalidade quando a base elementar é compatível. Afeto físico, expressão emocional e valores compartilhados criam uma sinergia que se aprofunda com o tempo. Momentos de crise são superados pela memória do que os une.`
    : score >= 60
    ? `O romance entre ${na} e ${nb} pode ser intenso e imprevisível. A atração existe, muitas vezes de forma magnética, mas sustentá-la requer trabalho consciente de ambos. A chave é transformar a tensão em combustível criativo.`
    : `${na} e ${nb} podem se atrair por aquilo que o outro representa, mas o relacionamento romântico pede paciência. As diferenças de ritmo, valores e necessidades emocionais aparecem cedo e exigem negociação contínua.`;

  const strengths = sameElement
    ? `Linguagem emocional compartilhada, valores naturalmente alinhados, facilidade de comunicação sem muita explicação, entusiasmo mútuo por objetivos comuns e capacidade de se reinventar juntos sem perder a identidade individual.`
    : score >= 70
    ? `A diferença que inicialmente parece desafio se transforma em complementaridade real. Um oferece o que o outro precisa. A parceria tem potencial de crescimento mútuo genuíno, cada um se torna mais inteiro na presença do outro.`
    : `Cada um oferece ao outro uma perspectiva que dificilmente encontraria sozinho. A relação pode expandir horizontes de ambos e gerar aprendizado profundo sobre tolerância, diferença e amor incondicional.`;

  const challenges = score >= 80
    ? `O maior risco é a competição interna, especialmente quando os dois precisam ser reconhecidos ao mesmo tempo. Aprender a celebrar as vitórias do parceiro com generosidade genuína é o trabalho constante da dupla.`
    : score >= 60
    ? `Ritmos diferentes de vida podem criar frustrações acumuladas. Um pode interpretar como rejeição o que o outro simplesmente vive como sua natureza. Clareza nas expectativas e flexibilidade mútua são essenciais.`
    : `Visões de mundo fundamentalmente diferentes podem criar impasses recorrentes. O risco é que a relação se torne um espaço de negociação constante em vez de descanso e crescimento. Requer maturidade emocional de ambos.`;

  const communication = score >= 80
    ? `A comunicação flui com naturalidade entre ${na} e ${nb}. O vocabulário emocional é similar o suficiente para evitar mal-entendidos frequentes. Discordâncias surgem, mas a capacidade de retornar ao diálogo costuma ser maior que o impulso de fechar.`
    : score >= 60
    ? `${na} e ${nb} se comunicam de formas diferentes, o que pode gerar interpretações equivocadas. Desenvolver a paciência de explicar o próprio funcionamento, sem julgamento, é o que transforma atrito em entendimento.`
    : `A comunicação é o maior campo de trabalho desta parceria. Estilos, ritmos e necessidades de diálogo são distintos. Com comprometimento, cada conversa difícil pode virar uma aula sobre si mesmo e sobre o outro.`;

  return {
    slug1: a,
    slug2: b,
    title: `${na} e ${nb}: Compatibilidade Astrológica`,
    score,
    verdict,
    metaDescription: `Descubra a compatibilidade entre ${na} e ${nb}: pontuação, amor, comunicação e desafios desta combinação astrológica.`,
    intro,
    love,
    strengths,
    challenges,
    communication,
  };
}

// Cache
const _cache: Record<string, CompatibilityData> = {};

/**
 * Returns compatibility data for two zodiac slugs.
 * Order does not matter, always returns the same result.
 */
export function getCompatibility(slug1: string, slug2: string): CompatibilityData | null {
  if (!slug1 || !slug2 || slug1 === slug2) return null;
  const key = [slug1, slug2].sort().join('-');
  if (!_cache[key]) {
    _cache[key] = buildCompatibility(slug1, slug2);
  }
  return _cache[key];
}
